import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  message,
  Popconfirm,
  Typography,
  Row,
  Col,
  Image,
  Tag,
  InputNumber
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  AppstoreOutlined,
  EyeOutlined,
  StarOutlined
} from '@ant-design/icons';
import { apiService } from '../../../services/api';
import type { Category, AdminAppListItem } from '../../../types';

const { Title } = Typography;
const { TextArea } = Input;

interface AppFormData {
  category_id: number;
  name: string;
  developer: string;
  icon_url: string;
  description: string;
  content_rating: string;
  android_download_url?: string;
  ios_download_url?: string;
  download_count?: number;
  average_rating?: number;
}

const Apps: React.FC = () => {
  const [apps, setApps] = useState<AdminAppListItem[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingApp, setEditingApp] = useState<AdminAppListItem | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<number | undefined>(undefined);
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [form] = Form.useForm();

  useEffect(() => {
    loadApps();
    loadCategories();
  }, []);

  const loadApps = async (page = 1, pageSize = 10, categoryId?: number, keyword?: string) => {
    try {
      setLoading(true);
      const params: any = {
        page,
        per_page: pageSize,
      };
      if (categoryId) {
        params.category_id = categoryId;
      }
      if (keyword && keyword.trim()) {
        params.search = keyword.trim();
      }
      const response = await apiService.adminGetApps(params);
      if (response.code === 0) {
        setApps(response.data.items);
        setPagination({
          current: page,
          pageSize,
          total: response.data.pagination.total_items,
        });
      } else {
        message.error(response.message || '获取APP列表失败');
      }
    } catch (error: any) {
      message.error('获取APP列表失败');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await apiService.getCategories();
      if (response.code === 0) {
        setCategories(response.data);
      }
    } catch (error: any) {
      message.error('获取分类列表失败');
    }
  };

  const handleCreate = () => {
    setEditingApp(null);
    setModalVisible(true);
    form.resetFields();
  };

  const handleEdit = (app: AdminAppListItem) => {
    setEditingApp(app);
    setModalVisible(true);
    form.setFieldsValue({
      category_id: app.category.id,
      name: app.name,
      developer: app.developer,
      icon_url: app.icon_url,
      description: app.description,
      content_rating: app.content_rating,
      android_download_url: app.android_download_url,
      ios_download_url: app.ios_download_url,
      download_count: app.download_count,
      average_rating: app.average_rating,
    });
  };

  const handleDelete = async (id: number) => {
    try {
      const response = await apiService.adminDeleteApp(id);
      if (response.code === 0) {
        message.success('删除成功');
        loadApps(pagination.current, pagination.pageSize, selectedCategory, searchKeyword);
      } else {
        message.error(response.message || '删除失败');
      }
    } catch (error: any) {
      message.error('删除失败');
    }
  };

  const handleSubmit = async (values: AppFormData) => {
    try {
      if (editingApp) {
        // 更新APP
        const response = await apiService.adminUpdateApp(editingApp.id, values);
        if (response.code === 0) {
          message.success('更新成功');
          setModalVisible(false);
          loadApps(pagination.current, pagination.pageSize, selectedCategory, searchKeyword);
        } else {
          message.error(response.message || '更新失败');
        }
      } else {
        // 创建APP
        const response = await apiService.adminCreateApp(values);
        if (response.code === 0) {
          message.success('创建成功');
          setModalVisible(false);
          loadApps(pagination.current, pagination.pageSize, selectedCategory, searchKeyword);
        } else {
          message.error(response.message || '创建失败');
        }
      }
    } catch (error: any) {
      message.error(editingApp ? '更新失败' : '创建失败');
    }
  };

  const handleTableChange = (paginationInfo: any) => {
    loadApps(paginationInfo.current, paginationInfo.pageSize, selectedCategory, searchKeyword);
  };

  const handleSortOrderChange = async (id: number, sortOrder: number) => {
    try {
      const response = await apiService.adminUpdateAppSortOrder(id, sortOrder);
      if (response.code === 0) {
        message.success('排序序号更新成功');
        loadApps(pagination.current, pagination.pageSize, selectedCategory, searchKeyword);
      } else {
        message.error(response.message || '更新排序序号失败');
      }
    } catch (error: any) {
      message.error('更新排序序号失败');
    }
  };

  const handleRecommendOrderChange = async (id: number, recommendOrder: number) => {
    try {
      const response = await apiService.adminUpdateAppRecommendOrder(id, recommendOrder);
      if (response.code === 0) {
        message.success('推荐序号更新成功');
        loadApps(pagination.current, pagination.pageSize, selectedCategory, searchKeyword);
      } else {
        message.error(response.message || '更新推荐序号失败');
      }
    } catch (error: any) {
      message.error('更新推荐序号失败');
    }
  };

  const handleCategoryFilter = (categoryId: number | undefined) => {
    setSelectedCategory(categoryId);
    loadApps(1, pagination.pageSize, categoryId, searchKeyword);
  };

  const handleSearch = (keyword: string) => {
    setSearchKeyword(keyword);
    loadApps(1, pagination.pageSize, selectedCategory, keyword);
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '图标',
      dataIndex: 'icon_url',
      key: 'icon_url',
      width: 80,
      render: (url: string) => (
        <Image
          src={url}
          alt="图标"
          width={40}
          height={40}
          style={{ borderRadius: 8 }}
          fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
        />
      ),
    },
    {
      title: 'APP名称',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => (
        <Space>
          <AppstoreOutlined />
          {text}
        </Space>
      ),
    },
    {
      title: '开发者',
      dataIndex: 'developer',
      key: 'developer',
      width: 150,
      render: (text: string) => <span>{text || '未知开发者'}</span>,
    },
    {
      title: '分类',
      dataIndex: 'category',
      key: 'category',
      render: (category: Category) => <Tag color="blue">{category.name}</Tag>,
    },
    {
      title: '内容评级',
      dataIndex: 'content_rating',
      key: 'content_rating',
      width: 120,
      render: (text: string) => <Tag color="green">{text || '全年龄段'}</Tag>,
    },
    {
      title: '下载量',
      dataIndex: 'download_count',
      key: 'download_count',
      width: 100,
      render: (count: number) => (
        <Space>
          <EyeOutlined />
          {count}
        </Space>
      ),
    },
    {
      title: '评分',
      dataIndex: 'average_rating',
      key: 'average_rating',
      width: 100,
      render: (rating: number) => (
        <Space>
          <StarOutlined />
          {rating.toFixed(1)}
        </Space>
      ),
    },
    {
      title: '排序序号',
      dataIndex: 'sort_order',
      key: 'sort_order',
      width: 120,
      render: (sortOrder: number, record: AdminAppListItem) => (
        <InputNumber
          min={0}
          max={9999}
          value={sortOrder}
          style={{ width: '100%' }}
          onBlur={(e) => {
            const newSortOrder = parseInt(e.target.value) || 0;
            if (newSortOrder !== sortOrder) {
              handleSortOrderChange(record.id, newSortOrder);
            }
          }}
        />
      ),
    },
    {
      title: '推荐序号',
      dataIndex: 'recommend_order',
      key: 'recommend_order',
      width: 120,
      render: (recommendOrder: number, record: AdminAppListItem) => (
        <InputNumber
          min={0}
          max={9999}
          value={recommendOrder}
          style={{ width: '100%' }}
          onBlur={(e) => {
            const newRecommendOrder = parseInt(e.target.value) || 0;
            if (newRecommendOrder !== recommendOrder) {
              handleRecommendOrderChange(record.id, newRecommendOrder);
            }
          }}
        />
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (text: string) => new Date(text).toLocaleString(),
    },
    {
      title: '操作',
      key: 'actions',
      width: 150,
      render: (_: any, record: AdminAppListItem) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个APP吗？"
            description="删除后不可恢复，请谨慎操作。"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Row gutter={[0, 24]}>
        <Col span={24}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Title level={2} style={{ margin: 0 }}>
              APP管理
            </Title>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              新增APP
            </Button>
          </div>
        </Col>
        
        <Col span={24}>
          <div style={{ marginBottom: 16, display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span>分类筛选：</span>
              <Select
                placeholder="选择分类"
                style={{ width: 200 }}
                allowClear
                value={selectedCategory}
                onChange={handleCategoryFilter}
              >
                {categories.map(category => (
                  <Select.Option key={category.id} value={category.id}>
                    {category.name}
                  </Select.Option>
                ))}
              </Select>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span>关键词搜索：</span>
              <Input.Search
                placeholder="输入关键词搜索APP名称或描述"
                style={{ width: 300 }}
                allowClear
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                onSearch={handleSearch}
                enterButton
              />
            </div>
          </div>
          <Table
            columns={columns}
            dataSource={apps}
            rowKey="id"
            loading={loading}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条记录`,
            }}
            onChange={handleTableChange}
          />
        </Col>
      </Row>

      <Modal
        title={editingApp ? '编辑APP' : '新增APP'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        destroyOnClose
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="APP名称"
                rules={[
                  { required: true, message: '请输入APP名称' },
                  { min: 2, message: 'APP名称至少2个字符' },
                ]}
              >
                <Input placeholder="请输入APP名称" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="developer"
                label="开发者"
              >
                <Input placeholder="请输入开发者名称" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="category_id"
                label="分类"
                rules={[{ required: true, message: '请选择分类' }]}
              >
                <Select placeholder="请选择分类">
                  {categories.map(category => (
                    <Select.Option key={category.id} value={category.id}>
                      {category.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="icon_url"
                label="图标URL"
                rules={[
                  { required: true, message: '请输入图标URL' },
                  { type: 'url', message: '请输入有效的URL' },
                ]}
              >
                <Input placeholder="请输入图标URL" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="描述"
          >
            <TextArea rows={3} placeholder="请输入APP描述" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="content_rating"
                label="内容评级"
              >
                <Select placeholder="请选择内容评级">
                  <Select.Option value="全年龄段">全年龄段</Select.Option>
                  <Select.Option value="3+">3+</Select.Option>
                  <Select.Option value="7+">7+</Select.Option>
                  <Select.Option value="12+">12+</Select.Option>
                  <Select.Option value="17+">17+</Select.Option>
                  <Select.Option value="18+">18+</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="download_count"
                label="下载量"
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="average_rating"
                label="评分"
              >
                <InputNumber min={0} max={5} step={0.1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="android_download_url"
            label="Android下载链接"
          >
            <Input placeholder="请输入Android下载链接" />
          </Form.Item>

          <Form.Item
            name="ios_download_url"
            label="iOS下载链接"
          >
            <Input placeholder="请输入iOS下载链接" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                {editingApp ? '更新' : '创建'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Apps;
